import React, { useRef, useState, useEffect, useMemo } from "react";
import { Canvas } from "fabric";
import EditCanvas from "./components/canvas/EditCanvas";
import EditBlock from "./components/canvas/EditBlock";
import PageSidebar from "./components/canvas/PageSidebar";
import LayoutStylePanel from "./components/canvas/LayoutStylePanel";
import useHistory from "./hooks/useHistory";
import {
  renderCanvasLayout,
  updateTextProps,
  updateButtonProps,
  isButtonGroup,
} from "./components/modules/fabricFunctionssec";
import {
  buildFlexMessageFromPages,
  buildOutboundPayload,
  normalizeMessages,
  validateMessages,
} from "./line/builder";
import { API_BASE_URL, UPLOAD_API_URL } from "./config";

const CARD_WIDTH = 360;

const FIXED_CANVAS_HEIGHT = 500; 
const MAX_PAGES = 12;

const defaultButtonConfig = {
  text: "按鈕",
  textColor: "#ffffff",
  bgColor: "#000000",
  fontSize: 18,
  linkUrl: "",
};

const createNewPage = () => ({
  id: Date.now().toString() + Math.random().toString(),
  layoutStyle: "standard",
  imageSrc: null,
  imageFile: null,
  imageMeta: null,
  canvasBg: "#ffffff",
  textProps: {
    text: "標題文字",
    color: "#000000",
    fontSize: 22,
    fontFamily: "Microsoft JhengHei",
  },
  buttons: [
    { ...defaultButtonConfig, text: "按鈕 1" },
    { ...defaultButtonConfig, text: "按鈕 2" },
  ],
});

const CanvasScreen = ({ userLineConfig = { token: "", userId: "" } }) => {
  const canvasElRef = useRef(null);
  const fabricCanvasRef = useRef(null);

  const { state: pages, set: setPages, undo, redo, canUndo, canRedo } = useHistory([createNewPage()]);
  const [activePageId, setActivePageId] = useState(pages[0].id);
  const [selectedObj, setSelectedObj] = useState(null);
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);

  const [lineConfig, setLineConfig] = useState({ sendMode: "test" });
  const [flexAltText, setFlexAltText] = useState("Flex Message");
  const [isProcessing, setIsProcessing] = useState(false);

  const activePage = pages.find((p) => p.id === activePageId) || pages[0];

  useEffect(() => {
    if (!pages.find((p) => p.id === activePageId)) {
      setActivePageId(pages[0].id);
    }
  }, [pages, activePageId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        e.shiftKey ? (canRedo && redo()) : (canUndo && undo());
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        canRedo && redo();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undo, redo, canUndo, canRedo]);

  const handleAddPage = () => {
    if (pages.length >= MAX_PAGES) return alert("已達頁面上限");
    const newPage = createNewPage();
    setPages([...pages, newPage]);
    setActivePageId(newPage.id);
  };

  const handleDeletePage = (id) => {
    if (pages.length <= 1) return;
    const newPages = pages.filter((p) => p.id !== id);
    setPages(newPages);
    if (id === activePageId) setActivePageId(newPages[0].id);
  };

  const handleReorderPages = (start, end) => {
    const res = Array.from(pages);
    const [removed] = res.splice(start, 1);
    res.splice(end, 0, removed);
    setPages(res);
  };

  const updateActivePage = (field, value) => {
    setPages((prev) =>
      prev.map((p) => (p.id === activePageId ? { ...p, [field]: value } : p))
    );
  };

  const updateActiveButton = (newProps) => {
    const newButtons = [...activePage.buttons];
    newButtons[selectedButtonIndex] = {
      ...newButtons[selectedButtonIndex],
      ...newProps,
    };
    updateActivePage("buttons", newButtons);
  };

  const handleImageSelect = (previewUrl, file, meta) => {
    setPages((prev) =>
      prev.map((p) =>
        p.id === activePageId
          ? { ...p, imageSrc: previewUrl, imageFile: file, imageMeta: meta }
          : p
      )
    );
  };

  useEffect(() => {
    const canvasEl = canvasElRef.current;
    if (!canvasEl) return;
    if (fabricCanvasRef.current) fabricCanvasRef.current.dispose();

    const canvas = new Canvas(canvasEl, {
      width: CARD_WIDTH,
      height: FIXED_CANVAS_HEIGHT,
      backgroundColor: activePage.canvasBg,
      selection: true,
    });
    fabricCanvasRef.current = canvas;

    renderCanvasLayout(canvas, activePage);

    const handleSelect = (e) => {
      const obj = e.selected[0];
      setSelectedObj(obj);
      if (obj && isButtonGroup(obj)) {
        setSelectedButtonIndex(
          obj.buttonIndex !== undefined ? obj.buttonIndex : 0
        );
      }
    };

    canvas.on("selection:created", handleSelect);
    canvas.on("selection:updated", handleSelect);
    canvas.on("selection:cleared", () => setSelectedObj(null));

    return () => {
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  }, [activePageId, activePage.layoutStyle]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    renderCanvasLayout(canvas, activePage);
  }, [activePage]);

  const updateAll = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    renderCanvasLayout(canvas, activePage);
  };

  const UPLOAD_TIMEOUT_MS = 30000;

  const uploadSingleImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
    try {
      console.log(`[上傳] 開始上傳圖片: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
      const res = await fetch(UPLOAD_API_URL, {
        method: "POST",
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const text = await res.text();
        console.error(`[上傳] 失敗: HTTP ${res.status}`, text);
        throw new Error(`上傳失敗 (HTTP ${res.status}): ${text.slice(0, 100)}`);
      }
      const data = await res.json();
      console.log(`[上傳] 成功: ${data.url || "(無 url)"}`);
      return data.url;
    } catch (e) {
      clearTimeout(timeoutId);
      if (e.name === "AbortError") {
        console.error(`[上傳] 逾時 (${UPLOAD_TIMEOUT_MS / 1000} 秒)`);
        throw new Error(`圖片上傳逾時，請檢查網路或稍後再試`);
      }
      console.error("[上傳] 錯誤:", e);
      throw e;
    }
  };

  const processPagesAndUpload = async () => {
    setIsProcessing(true);
    const newPages = [...pages];
    let hasChanges = false;
    const toUpload = newPages.filter((p) => p.imageFile && p.imageSrc?.startsWith("blob:"));
    console.log(`[上傳] 待上傳頁數: ${toUpload.length}`);
    try {
      await Promise.all(
        newPages.map(async (page, index) => {
          if (page.imageFile && page.imageSrc && page.imageSrc.startsWith("blob:")) {
            try {
              console.log(`[上傳] 第 ${index + 1} 頁開始上傳`);
              const publicUrl = await uploadSingleImage(page.imageFile);
              newPages[index] = {
                ...page,
                imageSrc: publicUrl,
                imageFile: null,
                imageMeta: page.imageMeta,
              };
              hasChanges = true;
              console.log(`[上傳] 第 ${index + 1} 頁完成`);
            } catch (e) {
              console.error(`[上傳] 第 ${index + 1} 頁失敗:`, e.message);
              throw new Error(`第 ${index + 1} 頁圖片上傳失敗: ${e.message}`);
            }
          }
        })
      );
      if (hasChanges) setPages(newPages);
      console.log("[上傳] 全部完成");
      return newPages;
    } catch (e) {
      console.error("[上傳] 流程失敗:", e);
      alert(e.message);
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  const SERVER_URL = `${API_BASE_URL}/api/send-line`;

  const handleSmartSendToLine = async () => {
    const finalPages = await processPagesAndUpload();
    if (!finalPages) return;

    if (!userLineConfig.token) return alert("⚠️ 請至設定填寫 Channel access token");
    if (lineConfig.sendMode === "test" && !userLineConfig.userId) return alert("⚠️ 請至設定填寫測試帳號 ID");

    const apiSendMode = lineConfig.sendMode === "test" ? "push" : "broadcast";
    const finalUserId = lineConfig.sendMode === "test" ? userLineConfig.userId : null;

    const flexMessage = buildFlexMessageFromPages({
      pages: finalPages,
      altText: flexAltText,
      allowLocalImage: false,
      includePreviewMeta: false,
    });
    const outboundMessages = normalizeMessages([{ ...flexMessage, id: "flex-live" }]);
    const errors = validateMessages(outboundMessages);
    if (errors.length) return alert(errors.join("\n"));

    const payload = buildOutboundPayload({
      messages: outboundMessages,
      token: userLineConfig.token,
      userId: finalUserId,
      sendMode: apiSendMode,
    });

    try {
      const res = await fetch(SERVER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.status === "success") alert("🎉 發送成功！");
      else alert("發送失敗: " + result.message);
    } catch (e) {
      alert("無法連接伺服器");
    }
  };

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, width: "100%", padding: 16, boxSizing: "border-box", overflow: "hidden" }}>
      {isProcessing && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          background: "rgba(0,0,0,0.7)", zIndex: 9999,
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", color: "#fff"
        }}>
          <div className="spinner" style={{ width: 40, height: 40, border: "4px solid #444", borderTop: "4px solid #3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite", marginBottom: 15 }}></div>
          <h2>處理圖片中...</h2>
        </div>
      )}

      <div style={{ flex: 1, display: "flex", gap: 16, overflow: "hidden", minWidth: 0, minHeight: 0, alignItems: "stretch" }}>
        {/* 左側：排版樣式 + 頁面管理（固定高度，內部滾動） */}
        <div style={{ width: 260, flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingRight: 4 }}>
            <LayoutStylePanel 
              layoutStyle={activePage.layoutStyle}
              setLayoutStyle={(style) => updateActivePage("layoutStyle", style)}
              onImageSelect={handleImageSelect}
            />
            <PageSidebar
              pages={pages}
              activePageId={activePageId}
              onSelectPage={setActivePageId}
              onAddPage={handleAddPage}
              onDeletePage={handleDeletePage}
              onReorderPages={handleReorderPages}
            />
          </div>
        </div>

        {/* 中央：訊息預覽（恢復原本樣式，加上側邊滑動條） */}
        <div style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start", // 💡 改為 flex-start，確保長畫布從頂部開始排版
          background: "rgba(30,30,30,0.5)",
          borderRadius: 16,
          border: "1px solid var(--border-color)",
          padding: 24,
          overflowY: "auto", // 💡 確保這裡能出現垂直滑動條
        }}>
          <div style={{ fontSize: 14, color: "#888", marginBottom: 16, fontWeight: 600, letterSpacing: "0.05em", flexShrink: 0 }}>
            訊息預覽
          </div>

          <div style={{ flexShrink: 0, paddingBottom: 40 }}> {/* 💡 加入底部留白，讓滑到最底時不會太貼齊邊緣 */}
            <EditCanvas
              imageSrc={activePage.imageSrc}
              imageMeta={activePage.imageMeta}
              canvasElRef={canvasElRef}
              CARD_WIDTH={CARD_WIDTH}
              CANVAS_HEIGHT={FIXED_CANVAS_HEIGHT}
              layoutStyle={activePage.layoutStyle}
            />
          </div>
        </div>

        {/* 右側：屬性設定（固定高度，底部按鈕固定） */}
        <div style={{ width: 300, display: "flex", flexDirection: "column", minHeight: 0, flexShrink: 0, overflow: "hidden" }}>
          <EditBlock
          canvasBg={activePage.canvasBg}
          setCanvasBg={(color) => updateActivePage("canvasBg", color)}
          selectedObj={selectedObj}
          textProps={activePage.textProps}
          setTextProps={(props) => {
            const newProps = { ...activePage.textProps, ...props };
            updateActivePage("textProps", newProps);
          }}
          buttonProps={activePage.buttons[selectedButtonIndex] || activePage.buttons[0]}
          setButtonProps={updateActiveButton}
          updateAll={updateAll}
          onSmartSend={handleSmartSendToLine}
          lineConfig={lineConfig}
          setLineConfig={setLineConfig}
          savedUserId={userLineConfig.userId}
          onUndo={undo}
          canUndo={canUndo}
          onRedo={redo}
          canRedo={canRedo}
          flexAltText={flexAltText}
          setFlexAltText={setFlexAltText}
          onPreview={undefined}
        />
        </div>
      </div>
    </div>
  );
};

export default CanvasScreen;